/**
 * Loads the 8th Wall Engine Binary (SLAM) from their CDN — "Option 1: Script
 * tag" per https://8thwall.org/docs/engine/overview, confirmed 2026-07-09.
 *
 * We use the CDN option rather than the npm package (Option 2) to avoid
 * adding a Vite copy-plugin step just for this one third-party binary;
 * revisit if offline/self-hosted delivery becomes a requirement.
 *
 * LICENSE: the engine binary (SLAM) is free to use commercially but is
 * closed-source, distributed under Niantic Spatial's XR Engine License
 * Agreement (see index.html for the required copyright notice — license
 * compliance needs it visible in view-source, which a plain <script src>
 * import to a public CDN file already satisfies).
 *
 * Loaded lazily, only when entering iOS camera mode — Android/WebXR users
 * never pay for this ~multi-MB SLAM chunk.
 */

const ENGINE_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/@8thwall/engine-binary@1/dist/xr.js';

// XR Extras: official 8th Wall utility pipeline modules (separate package
// from the engine binary itself). We only use FullWindowCanvas — it's the
// documented fix for camera canvases not filling the screen (see
// https://github.com/8thwall/web/blob/master/xrextras/README.md:
// "FullWindowCanvas.pipelineModule(): Makes sure that the camera display
// canvas fills the full browser window across device orientation changes,
// etc."). Confirmed real + published on jsdelivr 2026-07-09 (version 1.0.0).
// Root cause this fixes: 8th Wall's GlTextureRenderer/Threejs modules read
// the canvas's *drawing-buffer* size once during their own startup and
// don't re-sync it to the CSS layout size on their own — FullWindowCanvas
// is 8th Wall's own module for keeping those in sync, which plain CSS
// (width/height: 100%) does not do for a WebGL drawing buffer.
const XREXTRAS_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/@8thwall/xrextras@1/dist/xrextras.js';

declare global {
  interface Window {
    XR8?: XR8Global;
    XRExtras?: XRExtrasGlobal;
  }
}

export interface XRExtrasGlobal {
  FullWindowCanvas: { pipelineModule: () => XR8PipelineModule };
  [key: string]: unknown;
}

/**
 * Minimal typed surface of the global XR8 object — only what NavMe uses.
 * 8th Wall doesn't publish official TS types for the CDN build; the shapes
 * below are taken directly from confirmed API reference pages
 * (docs.8thwall.org/docs/api/engine/*), not guessed. Fields/methods we
 * don't use are intentionally left untyped rather than invented.
 */
export interface XR8Global {
  addCameraPipelineModules: (modules: XR8PipelineModule[]) => void;
  run: (opts: { canvas: HTMLCanvasElement; allowedDevices?: unknown }) => void;
  pause?: () => void;
  stop?: () => void;
  XrConfig: { device: () => { ANY: unknown } };
  GlTextureRenderer: { pipelineModule: () => XR8PipelineModule };
  XrController: { pipelineModule: () => XR8PipelineModule };
  Threejs: {
    pipelineModule: () => XR8PipelineModule;
    xrScene: () => {
      scene: import('three').Scene;
      camera: import('three').PerspectiveCamera;
      renderer: import('three').WebGLRenderer;
    };
  };
}

export interface XR8PipelineModule {
  name: string;
  onStart?: (args: { canvas: HTMLCanvasElement }) => void;
  onAttach?: () => void;
  onDetach?: () => void;
  onException?: (error: unknown) => void;
  onCameraStatusChange?: (args: { status: string }) => void;
  [key: string]: unknown;
}

let loadPromise: Promise<XR8Global> | null = null;

/** Injects the script tag once and resolves when window.XR8 is ready. */
export function loadEightWallEngine(): Promise<XR8Global> {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (window.XR8) {
      resolve(window.XR8);
      return;
    }
    // 8th Wall's own documented readiness signal (see getting-started sample).
    window.addEventListener('xrloaded', () => {
      if (window.XR8) resolve(window.XR8);
      else reject(new Error('xrloaded fired but window.XR8 is missing'));
    });

    const script = document.createElement('script');
    script.src = ENGINE_SCRIPT_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.setAttribute('data-preload-chunks', 'slam');
    script.onerror = () => reject(new Error('Failed to load 8th Wall engine script'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

let extrasLoadPromise: Promise<XRExtrasGlobal> | null = null;

/** Injects xrextras.js once and resolves when window.XRExtras is ready. */
export function loadEightWallExtras(): Promise<XRExtrasGlobal> {
  if (extrasLoadPromise) return extrasLoadPromise;

  extrasLoadPromise = new Promise((resolve, reject) => {
    if (window.XRExtras) {
      resolve(window.XRExtras);
      return;
    }
    // Documented readiness signal (see xrextras README's DebugWebViews sample:
    // `window.XRExtras ? screenlog() : window.addEventListener('xrextrasloaded', screenlog)`).
    window.addEventListener('xrextrasloaded', () => {
      if (window.XRExtras) resolve(window.XRExtras);
      else reject(new Error('xrextrasloaded fired but window.XRExtras is missing'));
    });

    const script = document.createElement('script');
    script.src = XREXTRAS_SCRIPT_URL;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Failed to load 8th Wall XRExtras script'));
    document.head.appendChild(script);
  });

  return extrasLoadPromise;
}

export function isEightWallLikelySupported(): boolean {
  // 8th Wall's own compatibility checks (LandingPage module) are more
  // thorough; this is a cheap pre-check so we don't attempt to load a
  // multi-MB script on a browser with no camera or WebGL at all.
  const hasCamera = Boolean(navigator.mediaDevices?.getUserMedia);
  const hasWebGL = (() => {
    try {
      const c = document.createElement('canvas');
      return Boolean(c.getContext('webgl') || c.getContext('webgl2'));
    } catch {
      return false;
    }
  })();
  return hasCamera && hasWebGL;
}
